import { useCallback, useEffect, useState } from "react";

import { onToast, type ToastMessage } from "../api/toastBus";

const isNetworkError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return lower.includes("failed to fetch") || lower.includes("network") || lower.includes("fetch");
};

export function useToastNotifications() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [closingToastIds, setClosingToastIds] = useState<Set<string>>(new Set());

  const dismissToast = useCallback((id: string) => {
    setClosingToastIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
      setClosingToastIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 220);
  }, []);

  useEffect(() => {
    const unsubscribe = onToast(toastMessage => {
      if (toastMessage.type === "error" && isNetworkError(toastMessage.message)) {
        return;
      }

      setToasts(prev => {
        const isDuplicate = prev.some(
          toast => toast.type === toastMessage.type && toast.message === toastMessage.message
        );
        if (isDuplicate) {
          return prev;
        }

        const autoCloseMs = toastMessage.type === "success" ? 4000 : 7000;
        setTimeout(() => {
          dismissToast(toastMessage.id);
        }, autoCloseMs);

        return [...prev, toastMessage];
      });
    });

    return unsubscribe;
  }, [dismissToast]);

  const dismissFirstToast = useCallback((): boolean => {
    if (toasts.length === 0) {
      return false;
    }
    dismissToast(toasts[toasts.length - 1].id);
    return true;
  }, [toasts, dismissToast]);

  return {
    closingToastIds,
    dismissFirstToast,
    dismissToast,
    hasAnyToasts: toasts.length > 0,
    toasts,
  };
}
